import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import metaPixelService from 'services/metaPixelService';

const MetaPixel = () => {
  const location = useLocation();
  const initializedRef = useRef(false);
  const shouldSkipRef = useRef(false);

  useEffect(() => {
    // Check if we should skip initialization (but don't return early)
    shouldSkipRef.current =
      window.__metaPixelInitialized && window.fbq && window.fbq.loaded;

    if (shouldSkipRef.current) {
      return;
    }

    // Use a more robust initialization check
    const shouldInitialize = () => {
      // Check if already initialized globally
      if (window.__metaPixelInitialized) {
        return false;
      }

      // Check if component already initialized
      if (initializedRef.current) {
        return false;
      }

      // Check if service is already initialized
      if (metaPixelService.isInitialized) {
        return false;
      }

      return true;
    };

    if (shouldInitialize()) {
      // Initialize Meta Pixel on mount (only once)
      metaPixelService.initialize();
      // Preserve fbclid for fbc cookie
      metaPixelService.preserveFbclid();
      initializedRef.current = true;
    }

    // Cleanup function to prevent memory leaks
    return () => {
      // Note: We don't destroy the Meta Pixel service as it's a singleton
      // and other components might still need it
    };
  }, []);

  useEffect(() => {
    // Track page view on route change (only if initialized and not skipped)
    if (
      !shouldSkipRef.current &&
      metaPixelService.isInitialized &&
      initializedRef.current
    ) {
      metaPixelService.trackPageView();
    }
  }, [location]);

  // Get appropriate Pixel ID for noscript fallback based on REACT_APP_URL
  const getPixelId = () => {
    // Detect environment based on REACT_APP_URL
    // Treat localhost and staging as staging environment
    const isStaging =
      process.env.REACT_APP_URL &&
      (process.env.REACT_APP_URL.includes('staging') ||
        process.env.REACT_APP_URL.includes('localhost'));

    const pixelId = isStaging
      ? process.env.REACT_APP_META_PIXEL_ID_STAGING || '24054315874260873'
      : process.env.REACT_APP_META_PIXEL_ID_PRODUCTION || '1805006363351321';

    return pixelId;
  };

  // If we're skipping initialization, render a minimal component
  if (shouldSkipRef.current) {
    return (
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${getPixelId()}&ev=PageView&noscript=1`}
          alt="Meta Pixel (Already Initialized)"
        />
      </noscript>
    );
  }

  return (
    <noscript>
      <img
        height="1"
        width="1"
        style={{ display: 'none' }}
        src={`https://www.facebook.com/tr?id=${getPixelId()}&ev=PageView&noscript=1`}
        alt="Meta Pixel"
      />
    </noscript>
  );
};

export default MetaPixel;
